'use client'

import { Control, Controller, FieldValues, Path } from 'react-hook-form'
import { useTranslations } from '@/lib/next-shim'
import { useParams } from '@/lib/next-shim'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import dayjs, { Dayjs } from 'dayjs'
import { getDateFormatForLocale } from '@/utils/dateUtils'

interface DateInputProps<T extends FieldValues> {
    colSpan: string
    id: Path<T>
    label: string
    name?: Path<T>
    control: Control<T>
    required?: boolean
    disabled?: boolean
    disableFuture?: boolean
}

export default function DateInput<T extends FieldValues>({
    colSpan,
    id,
    label,
    name,
    control,
    required = false,
    disabled = false,
    disableFuture = false
}: DateInputProps<T>) {
    const t = useTranslations()
    const params = useParams()
    const locale = (params?.lang as string) || 'fr' // fallback to French if locale not available

    // Set dayjs locale to match the current locale
    if (locale === 'en') {
        dayjs.locale('en')
    } else {
        dayjs.locale('fr')
    }

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={locale}>
            <Controller
                name={name ? name : id}
                control={control}
                render={({ field, fieldState }) => (
                    <div className={colSpan}>
                        <label htmlFor={id as string} className="block text-sm font-medium leading-6 text-gray-900">
                            {label}
                            {required && <span className="text-red-500">*</span>}
                        </label>
                        <DatePicker
                            value={field.value && dayjs(field.value).isValid() ? dayjs(field.value) : null}
                            disableFuture={disableFuture}
                            onChange={(newValue: Dayjs | null) => {
                                // Convert Dayjs object to ISO string for form submission
                                field.onChange(newValue && newValue.isValid() ? newValue.format('YYYY-MM-DD') : '')
                            }}
                            slotProps={{
                                textField: {
                                    id: id as string,
                                    error: !!fieldState.error,
                                    helperText: fieldState.error?.message,
                                    fullWidth: true,
                                    className: 'bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500',
                                    disabled
                                }
                            }}
                            format={getDateFormatForLocale(locale)}
                        />
                    </div>
                )}
            />
        </LocalizationProvider>
    )
}
